import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Image as ImageIcon, X, Bookmark, Check, RotateCw, ThumbsUp, AlertTriangle, Sparkles, Shirt, Layers, Footprints, Watch, Share2 } from "lucide-react";
import TopBar from "@/components/TopBar";
import RatingBar from "@/components/RatingBar";
import ScoreDonut from "@/components/ScoreDonut";
import Skeleton from "@/components/Skeleton";
import ShareModal from "@/components/ShareModal";
import api, { formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";
import { postWithRetry } from "@/lib/retryablePost";
import { useToast } from "@/components/Toast";

// Vibe presets — what aesthetic the user is going for. The AI rates how well
// the outfit matches the chosen vibe, NOT how attractive the person is.
// Order: most-common first.
const VIBES = [
  "Casual", "Streetwear", "Business Casual", "Old Money", "Luxury",
  "Date Night", "Y2K", "Gym", "Athleisure", "Summer", "Winter",
  "Minimalist", "Vintage", "Smart Casual", "Wedding Guest",
];

function fileToResizedBase64(file, maxDim = 1280) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        const s = Math.min(1, maxDim / Math.max(width, height));
        width = Math.round(width * s);
        height = Math.round(height * s);
        const c = document.createElement("canvas");
        c.width = width; c.height = height;
        c.getContext("2d").drawImage(img, 0, 0, width, height);
        resolve(c.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function RatingSkeleton() {
  return (
    <div data-testid="rate-loading" style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <div className="score-hero">
        <Skeleton w={84} h={84} r={999} />
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
          <Skeleton w="40%" h={12} />
          <Skeleton w="85%" h={22} />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 12, padding: "16px 0" }}>
        <div className="spinner" />
        <div className="tiny" style={{ letterSpacing: "0.22em", textTransform: "uppercase" }}>
          Analyzing your outfit…
        </div>
      </div>
      {[0,1,2,3,4,5].map((i) => (
        <div key={i} className="rating-row">
          <Skeleton w={110} h={12} />
          <Skeleton w="100%" h={6} r={999} />
          <Skeleton w={36} h={12} />
        </div>
      ))}
    </div>
  );
}

export default function RateMyOutfitScreen() {
  const navigate = useNavigate();
  const toast = useToast();
  const cameraInput = useRef(null);
  const galleryInput = useRef(null);

  const [step, setStep] = useState("upload"); // upload | analyzing | result | error
  const [imageBase64, setImageBase64] = useState(null);
  const [occasion, setOccasion] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const pickFromCamera = () => cameraInput.current?.click();
  const pickFromGallery = () => galleryInput.current?.click();

  const onFile = async (e) => {
    setError("");
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!/image\/(jpeg|png|webp|jpg)/i.test(file.type)) {
      setError("Please choose a JPEG, PNG, or WEBP image.");
      return;
    }
    try {
      const b64 = await fileToResizedBase64(file, 1280);
      setImageBase64(b64);
    } catch {
      setError("Could not process that image.");
    }
  };

  const analyze = async () => {
    if (!imageBase64) {
      setError("Add a photo of your outfit first.");
      return;
    }
    setStep("analyzing");
    setError("");
    setData(null);
    setSaved(false);
    try {
      const res = await postWithRetry("/outfit/rate", {
        image_base64: imageBase64,
        image_mime: "image/jpeg",
        occasion: occasion || null,
      }, { scope: "outfit-rate", timeout: 90_000 });
      setData(res);
      setStep("result");
      try { track("feature_completed", { feature: "style", sub_action: "outfit_rated" }); } catch { /* ignore */ }
    } catch (err) {
      setError(formatApiError(err));
      setStep("error");
    }
  };

  const reset = () => {
    setStep("upload");
    setData(null);
    setError("");
    setSaved(false);
  };

  const save = async () => {
    if (!data?.rating) return;
    setSaving(true);
    try {
      await api.post("/ratings/save", {
        rating: data.rating,
        thumbnail_base64: imageBase64,
        occasion: occasion || null,
      });
      setSaved(true);
      toast.show("Rating saved to your history", "success");
    } catch (err) {
      toast.show(formatApiError(err), "error");
    } finally {
      setSaving(false);
    }
  };

  const rating = data?.rating;
  const scores = rating?.scores || {};

  return (
    <div className="screen fade-in" data-testid="rate-screen">
      <TopBar
        title="RATE MY OUTFIT"
        right={
          step === "result" ? (
            <button className="icon-btn" onClick={reset} aria-label="Rate another" data-testid="rate-reset-btn">
              <RotateCw size={16} strokeWidth={1.7} />
            </button>
          ) : null
        }
      />

      {/* hidden inputs */}
      <input
        ref={cameraInput} type="file" accept="image/jpeg,image/png,image/webp" capture="environment"
        onChange={onFile} style={{ display: "none" }} data-testid="rate-camera-input"
      />
      <input
        ref={galleryInput} type="file" accept="image/jpeg,image/png,image/webp"
        onChange={onFile} style={{ display: "none" }} data-testid="rate-gallery-input"
      />

      {/* ---------------- UPLOAD STEP ---------------- */}
      {step === "upload" && (
        <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ marginBottom: 6 }}>
            <div className="eyebrow" style={{ marginBottom: 10 }}>HONEST FEEDBACK</div>
            <h2 className="h1" style={{ fontSize: 36 }}>
              Rate your <em className="serif" style={{ fontStyle: "italic", fontWeight: 300 }}>fit</em>
            </h2>
            <p className="body" style={{ marginTop: 12 }}>
              Upload a photo and Nova will score it across 6 dimensions, then tell you what to keep and what to fix.
            </p>
          </div>

          {imageBase64 ? (
            <div style={{ position: "relative" }}>
              <img src={imageBase64} alt="your outfit" className="preview-img" data-testid="rate-preview-img" />
              <button
                type="button"
                className="icon-btn"
                onClick={() => setImageBase64(null)}
                aria-label="Remove"
                data-testid="rate-clear-btn"
                style={{ position: "absolute", top: 12, right: 12, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)" }}
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <button
                type="button"
                className="tile"
                onClick={pickFromCamera}
                data-testid="rate-tile-camera"
                style={{ flexDirection: "row", alignItems: "center", textAlign: "left" }}
              >
                <div className="tile-icon"><Camera size={20} strokeWidth={1.7} /></div>
                <div style={{ flex: 1 }}>
                  <div className="h3">Take a photo</div>
                  <div className="tiny" style={{ marginTop: 4, color: "rgba(255,255,255,0.5)" }}>Open camera</div>
                </div>
              </button>
              <button
                type="button"
                className="tile"
                onClick={pickFromGallery}
                data-testid="rate-tile-gallery"
                style={{ flexDirection: "row", alignItems: "center", textAlign: "left" }}
              >
                <div className="tile-icon"><ImageIcon size={20} strokeWidth={1.7} /></div>
                <div style={{ flex: 1 }}>
                  <div className="h3">Pick from gallery</div>
                  <div className="tiny" style={{ marginTop: 4, color: "rgba(255,255,255,0.5)" }}>JPEG, PNG, or WEBP</div>
                </div>
              </button>
            </div>
          )}

          <div className="field" style={{ marginTop: 10 }}>
            <label className="label" htmlFor="occ">What vibe are you going for?</label>
            <input
              id="occ"
              className="input"
              placeholder="e.g. Streetwear, Old Money, Date Night"
              value={occasion}
              onChange={(e) => setOccasion(e.target.value)}
              data-testid="rate-occasion-input"
            />
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {VIBES.map((o) => (
              <button
                key={o}
                type="button"
                className={`chip ${occasion === o ? "active" : ""}`}
                onClick={() => setOccasion(o)}
                data-testid={`rate-vibe-${o.replace(/\s+/g, "-").toLowerCase()}`}
              >
                {o}
              </button>
            ))}
          </div>

          {error && <div className="error-text" data-testid="rate-error">{error}</div>}

          <button
            className="btn btn-primary"
            disabled={!imageBase64}
            onClick={analyze}
            data-testid="rate-analyze-btn"
            style={{ marginTop: 14 }}
          >
            <Sparkles size={16} /> Analyze my outfit
          </button>
        </div>
      )}

      {/* ---------------- ANALYZING ---------------- */}
      {step === "analyzing" && <RatingSkeleton />}

      {/* ---------------- ERROR ---------------- */}
      {step === "error" && (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: 18 }} data-testid="rate-error-state">
          <div className="eyebrow" style={{ color: "var(--danger)" }}>SOMETHING WENT WRONG</div>
          <p className="body" style={{ color: "#fff" }}>{error}</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="btn btn-outline" onClick={analyze} data-testid="rate-retry-btn">Try again</button>
            <button className="btn btn-ghost" onClick={reset} data-testid="rate-restart-btn">Start over</button>
          </div>
        </div>
      )}

      {/* ---------------- RESULT ---------------- */}
      {step === "result" && rating && (
        <div className="stagger" style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {/* Score hero */}
          <div className="score-hero" data-testid="rate-score-hero">
            <ScoreDonut score={scores.overall ?? 0} testId="rate-score-donut" />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="eyebrow" style={{ marginBottom: 6 }}>
                {(data?.occasion || "OUTFIT").toString().toUpperCase()}
              </div>
              <div className="tiny" style={{ color: "var(--text-faint)", letterSpacing: "0.16em", marginTop: 2 }}>
                {data?.occasion ? "VIBE MATCH" : "OUTFIT REVIEW"}
              </div>
              <div className="h2" style={{ fontSize: 24, lineHeight: 1.15 }} data-testid="rate-title">
                {rating.title || "Your outfit"}
              </div>
            </div>
          </div>

          {imageBase64 && (
            <div className="outfit-section" data-testid="rate-photo-section">
              <div className="eyebrow" style={{ marginBottom: 12 }}>YOUR PHOTO</div>
              <img src={imageBase64} alt="" className="preview-img" style={{ maxHeight: 320 }} />
            </div>
          )}

          {rating.summary && (
            <div className="outfit-section" data-testid="rate-summary-section">
              <div className="eyebrow" style={{ marginBottom: 10 }}>HONEST CRITIQUE</div>
              <p className="body" style={{ color: "#fff" }} data-testid="rate-summary">{rating.summary}</p>
            </div>
          )}

          {/* Rating bars */}
          <div className="outfit-section" data-testid="rate-scores-section">
            <div className="eyebrow" style={{ marginBottom: 14 }}>SCORE BREAKDOWN</div>
            <div>
              {scores.style != null && <RatingBar label="Style" score={scores.style} testId="rate-bar-style" />}
              {scores.fit != null && <RatingBar label="Fit" score={scores.fit} testId="rate-bar-fit" />}
              {scores.color_coordination != null && <RatingBar label="Color" score={scores.color_coordination} testId="rate-bar-color" />}
              {scores.trendiness != null && <RatingBar label="Trendiness" score={scores.trendiness} testId="rate-bar-trend" />}
              {scores.occasion_match != null && <RatingBar label="Occasion" score={scores.occasion_match} testId="rate-bar-occasion" />}
            </div>
          </div>

          {(rating.strengths || []).length > 0 && (
            <div className="outfit-section" data-testid="rate-strengths-section">
              <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <ThumbsUp size={12} strokeWidth={1.8} /> STRENGTHS
              </div>
              <ul className="bullet-list">
                {rating.strengths.map((s, i) => <li key={i} data-testid={`rate-strength-${i}`}>{s}</li>)}
              </ul>
            </div>
          )}

          {(rating.weaknesses || []).length > 0 && (
            <div className="outfit-section" data-testid="rate-weaknesses-section">
              <div className="eyebrow" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
                <AlertTriangle size={12} strokeWidth={1.8} /> LEVEL-UP NOTES
              </div>
              <ul className="bullet-list warn">
                {rating.weaknesses.map((s, i) => <li key={i} data-testid={`rate-weakness-${i}`}>{s}</li>)}
              </ul>
            </div>
          )}

          {/* Per launch spec (2026-02-26): the Rate flow is rating-only.
              No Find Similar, no shopping section, no nearby stores, no map.
              Users get the rating + a soft invitation to upload another. */}

          {/* Soft invitation — per master spec (2026-02-26): instead of
              telling the user what to wear, simply invite another upload. */}
          <div
            className="outfit-section"
            data-testid="rate-try-another-card"
            style={{
              padding: 16,
              borderRadius: 16,
              border: "1px solid var(--gold-line)",
              background: "var(--gold-soft)",
              textAlign: "center",
            }}
          >
            <div className="tiny" style={{ letterSpacing: "0.22em", textTransform: "uppercase", color: "var(--gold)" }}>
              CURIOUS?
            </div>
            <div className="serif" style={{ fontFamily: "'Fraunces', serif", fontSize: 19, color: "var(--text)", marginTop: 6, lineHeight: 1.3 }}>
              Want to see if another outfit matches your vibe even better?
            </div>
            <button
              type="button"
              className="btn btn-gold"
              onClick={reset}
              data-testid="rate-try-another-btn"
              style={{ marginTop: 12, width: "100%", justifyContent: "center" }}
            >
              Upload another outfit
            </button>
          </div>

          <div style={{ marginTop: 22, display: "flex", flexDirection: "column", gap: 10 }}>
            <button
              className="btn btn-primary"
              onClick={save}
              disabled={saving || saved}
              data-testid="rate-save-btn"
            >
              {saved
                ? (<><Check size={16} /> Saved to history</>)
                : (<><Bookmark size={16} /> {saving ? "Saving…" : "Save to history"}</>)}
            </button>
            <button
              className="btn btn-outline"
              onClick={() => setShareOpen(true)}
              data-testid="rate-share-btn"
            >
              <Share2 size={16} /> Share rating
            </button>
            <button className="btn btn-outline" onClick={reset} data-testid="rate-another-btn">
              Rate another outfit
            </button>
            <button className="btn btn-ghost" onClick={() => navigate("/home")} data-testid="rate-home-btn">
              Back home
            </button>
          </div>

          <ShareModal
            open={shareOpen}
            onClose={() => setShareOpen(false)}
            payload={{
              title: rating.title || "My outfit",
              overall: scores.overall ?? 0,
              breakdown: [
                { label: "Style", score: scores.style },
                { label: "Fit", score: scores.fit },
                { label: "Color", score: scores.color_coordination },
                { label: "Trendiness", score: scores.trendiness },
                { label: "Occasion", score: scores.occasion_match },
              ].filter((b) => b.score != null),
              summary: rating.summary || "",
              photoBase64: imageBase64,
            }}
          />
        </div>
      )}
    </div>
  );
}
